import * as React from 'react';
import {
  Checkbox,
  Input,
  InputWrapper,
} from 'vet-bones/design-system/components';

import { SecretInput } from 'src/ui/components/SecretInput';
import {
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_ID,
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET,
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_REPLACE,
  CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_VIEW,
  CONFIGURE_ORG_SSO_DIALOG_DISCOVERY_URL,
  CONFIGURE_ORG_SSO_DIALOG_IDP_LOGOUT,
} from 'src/ui/constants/testIds';
import { OrganizationMicrosoftEntraSsoConfig } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';

export interface EntraFormData {
  discoveryUrl: string;
  clientId: string;
  clientSecret: string;
  idpLogoutEnabled: boolean;
}

export const getInitialEntraFormData = (): EntraFormData => ({
  discoveryUrl: '',
  clientId: '',
  clientSecret: '',
  idpLogoutEnabled: false,
});

export const populateEntraForm = (
  config: OrganizationMicrosoftEntraSsoConfig
): EntraFormData => ({
  discoveryUrl: config.discoveryUrl,
  clientId: config.clientId,
  clientSecret: '',
  idpLogoutEnabled: config.idpLogoutEnabled,
});

export const buildEntraCreateInput = (form: EntraFormData) => ({
  discoveryUrl: form.discoveryUrl.trim(),
  clientId: form.clientId.trim(),
  clientSecret: form.clientSecret.trim(),
  idpLogoutEnabled: form.idpLogoutEnabled,
});

export const buildEntraUpdateInput = (form: EntraFormData) => {
  const input: Record<string, string | boolean> = {
    discoveryUrl: form.discoveryUrl.trim(),
    clientId: form.clientId.trim(),
    idpLogoutEnabled: form.idpLogoutEnabled,
  };
  if (form.clientSecret.trim()) {
    input.clientSecret = form.clientSecret.trim();
  }
  return input;
};

export const isEntraCreateValid = (form: EntraFormData) =>
  form.discoveryUrl.trim() !== '' &&
  form.clientId.trim() !== '' &&
  form.clientSecret.trim() !== '';

export const isEntraEditValid = (form: EntraFormData) =>
  form.discoveryUrl.trim() !== '' && form.clientId.trim() !== '';

export const isEntraEditDirty = (
  form: EntraFormData,
  config: OrganizationMicrosoftEntraSsoConfig
) =>
  form.discoveryUrl !== config.discoveryUrl ||
  form.clientId !== config.clientId ||
  form.clientSecret !== '' ||
  form.idpLogoutEnabled !== config.idpLogoutEnabled;

interface ConfigureOrgSSOEntraFormProps {
  form: EntraFormData;
  isCreate: boolean;
  onChange: (form: EntraFormData) => void;
}

export const ConfigureOrgSSOEntraForm: React.VFC<ConfigureOrgSSOEntraFormProps> = ({
  form,
  isCreate,
  onChange,
}) => (
  <>
    <Input
      className={isCreate ? 'organization-dialog-input-help-icon-hidden' : ''}
      clearable={false}
      helpProps={{
        text: isCreate
          ? copy.components.configureOrgSSO.dialog.entra.discoveryUrl.helpCreate
          : copy.components.configureOrgSSO.dialog.entra.discoveryUrl.helpEdit,
      }}
      label={copy.components.configureOrgSSO.dialog.entra.discoveryUrl.label}
      onChange={(e) => onChange({ ...form, discoveryUrl: e.target.value })}
      placeholder={
        copy.components.configureOrgSSO.dialog.entra.discoveryUrl.placeholder
      }
      required
      size="md"
      testId={CONFIGURE_ORG_SSO_DIALOG_DISCOVERY_URL}
      value={form.discoveryUrl}
    />
    <Input
      className="organization-dialog-input-help-icon-hidden"
      clearable={false}
      helpProps={{
        text: copy.components.configureOrgSSO.dialog.entra.clientId.help,
      }}
      label={copy.components.configureOrgSSO.dialog.entra.clientId.label}
      onChange={(e) => onChange({ ...form, clientId: e.target.value })}
      required
      size="md"
      testId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_ID}
      value={form.clientId}
    />
    <SecretInput
      cancelText={copy.components.configureOrgSSO.dialog.secretInput.cancel}
      className="organization-dialog-input-help-icon-hidden"
      configuredText={
        copy.components.configureOrgSSO.dialog.secretInput.configured
      }
      hasExistingValue={!isCreate}
      helpText={copy.components.configureOrgSSO.dialog.secretInput.help}
      initialValue={form.clientSecret}
      inputTestId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET}
      label={copy.components.configureOrgSSO.dialog.entra.clientSecret.label}
      onReplace={(clientSecret) => onChange({ ...form, clientSecret })}
      replaceTestId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_REPLACE}
      replaceText={copy.components.configureOrgSSO.dialog.secretInput.replace}
      required={isCreate}
      viewTestId={CONFIGURE_ORG_SSO_DIALOG_CLIENT_SECRET_VIEW}
    />
    <div className="organization-dialog-divider" />
    <InputWrapper
      className="organization-dialog-input-help-icon-hidden"
      classNamePrefix="vb-input"
      helpProps={{
        text: copy.components.configureOrgSSO.dialog.entra.idpLogout.help,
      }}
      label=""
      labelSize="md"
      size="md"
    >
      <Checkbox
        checked={form.idpLogoutEnabled}
        onChange={() =>
          onChange({ ...form, idpLogoutEnabled: !form.idpLogoutEnabled })
        }
        size="md"
        testId={CONFIGURE_ORG_SSO_DIALOG_IDP_LOGOUT}
      >
        {copy.components.configureOrgSSO.dialog.entra.idpLogout.label}
      </Checkbox>
    </InputWrapper>
  </>
);
